import { motion } from "framer-motion";
import { GraduationCap, School, BookOpen, CalendarDays } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading";

// Education details array
const educationDetails = [
  {
    level: "Bachelor of Technology",
    institution: "Lovely Professional University, Punjab",
    year: "Aug 2016 – May 2019",
    degree: "Mechanical Engineer",
    icon: <GraduationCap size={16} />,
    current: true, // Pulses like the most recent Experience
  },
  {
    level: "Diploma in Mechanical Engineering",
    institution: "Guru Nanak Dev Co-Ed. Polytechnic, New Delhi",
    year: "August 2013 – May 2016",
    degree: null,
    icon: <School size={16} />,
    current: false,
  },
  {
    level: "X (CBSE)",
    institution: "Rajkiya Sarvodaya Bal Vidyalaya, New Delhi",
    year: "June 2012 – March 2013",
    degree: null,
    icon: <BookOpen size={16} />,
    current: false,
  },
];

/* ─── Timeline Dot ─── */
const TimelineDot = ({ isCurrent }) => (
  <div className="relative flex items-center justify-center flex-shrink-0">
    {isCurrent ? (
      <>
        <span className="absolute w-5 h-5 rounded-full bg-cyan-400/50 dark:bg-cyan-400/30 animate-ping" />
        <span className="relative w-3 h-3 rounded-full bg-cyan-500 dark:bg-cyan-400 ring-[3px] ring-cyan-100 dark:ring-cyan-900/50 shadow-lg shadow-cyan-500/30" />
      </>
    ) : (
      <span className="w-3 h-3 rounded-full bg-blue-400 dark:bg-blue-500 ring-[3px] ring-blue-100 dark:ring-blue-900/40 shadow-sm" />
    )}
  </div>
);

/* ─── Education Card (Exact Match to Experience Collapsed State) ─── */
const EducationCard = ({ edu }) => (
  <motion.div
    initial={{ opacity: 0, y: 15 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.3, ease: "easeInOut" }}
    className="rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all duration-300 bg-white dark:bg-gray-800"
  >
    {/* Header (Exact same padding and layout as Experience) */}
    <div className="flex items-start justify-between gap-4 px-4 sm:px-5 py-4">
      {/* Left content */}
      <div className="flex flex-col gap-1 min-w-0">
        
        {/* Top row: Icon + Institution (Like Company) */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-blue-600 dark:text-blue-400 flex-shrink-0">
            {edu.icon}
          </span>
          <span className="text-blue-700 dark:text-blue-400 font-semibold text-sm">
            {edu.institution}
          </span>
        </div>

        {/* Level / Degree (Like Role) */}
        <p className="font-bold text-gray-800 dark:text-white text-sm sm:text-base leading-snug">
          {edu.degree ? `${edu.level} (${edu.degree})` : edu.level}
        </p>

        {/* Duration */}
        <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs mt-0.5">
          <CalendarDays size={13} />
          <span>{edu.year}</span>
        </div>

      </div>
    </div>
  </motion.div>
);

/* ─── Main Education Section ─── */
const Education = () => {

  return (
    <section
      id="education"
      className="pt-4 pb-10 px-4 sm:px-6 md:px-16 bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto max-w-screen-xl">
        <SectionHeading
          title="Education"
          subtitle="My academic journey"
          gradient="blue"
        />

        {/* Timeline layout matching Experience */}
        <div className="flex flex-col">
          {educationDetails.map((edu, index) => {
            const isLast = index === educationDetails.length - 1;
            return (
              <div key={index} className="flex gap-3 md:gap-5">
                
                {/* Timeline rail: hidden on mobile, visible on md+ */}
                <div className="hidden md:flex flex-col items-center pt-5 self-stretch">
                  <TimelineDot isCurrent={edu.current} />
                  {!isLast && (
                    <div className="w-px flex-1 mt-1 border-l-2 border-dashed border-gray-300 dark:border-gray-600" />
                  )}
                </div>

                {/* Card Container - Spans Full Width exactly like Experience */}
                <div className={`flex-1 min-w-0 ${!isLast ? "pb-6" : ""}`}>
                  <EducationCard edu={edu} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Education;